import uuid from 'uuid-random';

import {
  ApplicationContext,
  ApplicationContextBase,
} from '../../../applications/bridge/v2/types/bridgeTypesV2';
import {toSubscribableCancelToken} from '../../../helpers/asyncCancelHelpers';
import {serializeTeammateForApplication} from '../../../helpers/models/applicationSerializationHelpers';
import {ApplicationPreferencesModel} from '../../../models/api/applicationPreferencesModel';
import {TeammateModel} from '../../../models/api/teammateModel';
import {WidgetTemplateModel} from '../../../models/applications/widgetTemplateModel';
import {Dispatch} from '../../../store/appStore';
import {applicationListChannelsAction} from '../../../store/data/applications/applicationsChannelsActions';
import {applicationListInboxesAction} from '../../../store/data/applications/applicationsInboxesActions';
import {
  applicationComposeMessageAsyncAction,
  applicationUpdateDraftAction,
} from '../../../store/data/applications/applicationsMessagesActions';
import {applicationListTagsAsyncAction} from '../../../store/data/applications/applicationsTagsActions';
import {applicationListTeammatesAction} from '../../../store/data/applications/applicationsTeammateActions';
import {
  applicationSearchAction,
  authenticateLegacyApplicationAction,
  deauthenticateLegacyApplicationAction,
  relayLegacyApplicationHttpRequestAsyncAction,
  sendLegacyApplicationHttpRequestAsyncAction,
} from '../../../store/data/applications/legacyApplicationsActions';
import {
  legacyApplicationOpenUrlAction,
  legacyApplicationOpenUrlInPopupAction,
} from '../../../store/data/applications/legacyApplicationsBrowserActions';

interface ContextBuilderParameters {
  dispatch: Dispatch;
  user: TeammateModel;
  applicationId: number;
  entryPointId: string;
  hostId: string;
  preferences: object;
  authentication: ApplicationPreferencesModel['authentication'];
  addWidget: (widget: WidgetTemplateModel) => void;
  removeWidget: (widgetId: string) => void;
}

export type LegacyContextBuilder = (parameters: ContextBuilderParameters) => ApplicationContext;

export function buildBaseContext(parameters: ContextBuilderParameters): ApplicationContextBase {
  const {dispatch, applicationId} = parameters;
  const {entryPointId, hostId, preferences, user, addWidget, removeWidget, authentication} = parameters;

  return {
    id: uuid(),
    entryPointId,
    hostId,
    functionArities: {}, // Will be filled dynamically right before sending the context.
    preferences,
    authentication,

    teammate: serializeTeammateForApplication(user),
    createWidget: async (template) => {
      addWidget(template);
      return {} as any;
    },

    destroyWidget: async (widgetId) => {
      removeWidget(widgetId);
    },

    sendHttp: (request, cancelToken) =>
      dispatch(
        sendLegacyApplicationHttpRequestAsyncAction(
          applicationId,
          request,
          toSubscribableCancelToken(cancelToken),
        ),
      ),

    relayHttp: (request, cancelToken) =>
      dispatch(
        relayLegacyApplicationHttpRequestAsyncAction(
          applicationId,
          request,
          toSubscribableCancelToken(cancelToken),
        ),
      ),

    authenticate: () => dispatch(authenticateLegacyApplicationAction(applicationId)),

    deauthenticate: () => dispatch(deauthenticateLegacyApplicationAction(applicationId)),

    search: async (query) => {
      dispatch(applicationSearchAction(query));
    },

    listTeammates: async (paginationToken) => dispatch(applicationListTeammatesAction(paginationToken)),

    listInboxes: async (paginationToken) => dispatch(applicationListInboxesAction(paginationToken)),

    listChannels: async (paginationToken) => dispatch(applicationListChannelsAction(paginationToken)),

    listTags: (paginationToken) => dispatch(applicationListTagsAsyncAction(paginationToken)),

    openUrl: async (url) => {
      dispatch(legacyApplicationOpenUrlAction(applicationId, url));
    },

    openUrlInPopup: async (url, options) => {
      dispatch(legacyApplicationOpenUrlInPopupAction(applicationId, url, options));
    },

    createDraft: (draftTemplate) => {
      if (draftTemplate.replyOptions) {
        throw new Error('Unable to create a reply draft from this context.');
      }

      return dispatch(applicationComposeMessageAsyncAction(draftTemplate));
    },

    updateDraft: async (draftId, update) => {
      await dispatch(applicationUpdateDraftAction(draftId, update));
    },
  };
}
